import {keyframes} from '@mui/material'
import type {SxProps, Theme} from '@mui/material'

export const borderAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`

/**
 * The frame the agent's in-progress states share: the tool line, and the pause
 * while an attachment finishes being indexed (AB#5992).
 *
 * Shared rather than copied because the frame is what marks a line as "the
 * agent, still working" — a second one that drifted would read as a different
 * kind of message, which is exactly how the waiting line first shipped: correct
 * text, no frame, and it looked like stray page copy.
 */
export const liveStatusSx = (
  theme: Theme,
  tone: 'default' | 'incident',
  showBorder: boolean,
): SxProps<Theme> => ({
  mt: 1,
  p: 1,
  maxWidth: '75%',
  alignSelf: 'flex-start',
  borderRadius: 2,
  bgcolor: tone === 'incident' ? 'transparent' : 'background.paper',
  color: 'text.secondary',
  backdropFilter: tone === 'incident' ? 'none' : 'blur(6px)',
  overflow: 'hidden',
  boxShadow: tone === 'incident' ? 'none' : theme.shadows[2],
  position: 'relative',
  ...(showBorder && {
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      padding: '1.5px',
      background: theme.palette.background.aiGradient,
      backgroundSize: '300% 300%',
      animation: `${borderAnimation} 4s linear infinite`,
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      pointerEvents: 'none',
      zIndex: -1,
    },
  }),
})

/** The mountain's "still working" pulse, on both. */
export const livePulseSx = {
  animation: 'pulse 1.5s ease-in-out infinite',
  '@keyframes pulse': {
    '0%': {transform: 'scale(1)', opacity: 1},
    '50%': {transform: 'scale(1.06)', opacity: 0.85},
    '100%': {transform: 'scale(1)', opacity: 1},
  },
} as const
